import React from 'react';
import { EDSButton } from '@gehc-eds-react/eds-button';
 
// ── Helpers (kept local since only this modal uses them) ──
 
const CHECK_LABELS = {
    source_registered: 'Source registered',
    cross_account_trust: 'Assume cross-account role',
    source_bucket_access: 'Source bucket access',
    s3control_permissions: 'S3 control permissions',
    dest_bucket_policy: 'Destination write access',
    kms_key_policy: 'KMS key policy'
};
 
const humanizeCheckName = (name) => {
    if (CHECK_LABELS[name]) return CHECK_LABELS[name];
    if (!name) return 'Check';
    const spaced = name.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
 
const STATUS_TONE = {
    pass: { label: 'Passed', color: '#139c48', backgroundColor: '#e8f5e9' },
    fail: { label: 'Failed', color: '#c62828', backgroundColor: '#ffebee' },
    skip: { label: 'Skipped', color: '#8a8f98', backgroundColor: '#f3f4f6' },
    skipped: { label: 'Skipped', color: '#8a8f98', backgroundColor: '#f3f4f6' }
};
 
const getCheckStatusTone = (status) => {
    const key = (status || '').toLowerCase();
    if (STATUS_TONE[key]) return STATUS_TONE[key];
    return { label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown', color: '#8a8f98', backgroundColor: '#f3f4f6' };
};
 
const getOverallTone = (overall) => {
    const key = (overall || '').toLowerCase();
    if (key === 'pass' || key === 'healthy') return { color: '#139c48', backgroundColor: '#e8f5e9' };
    if (key === 'fail' || key === 'failed') return { color: '#c62828', backgroundColor: '#ffebee' };
    return { color: '#b7791f', backgroundColor: '#fdf3e1' };
};
 
const formatCheckedAt = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};
 
const CheckRow = ({ check, index, isExpanded, onToggleDetails }) => {
    const tone = getCheckStatusTone(check.status);
    const isFailed = (check.status || '').toLowerCase() === 'fail';
    const hasDetails = Boolean(check.hint || check.error_code || check.action);
 
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '14px 4px',
                borderTop: index === 0 ? 'none' : '1px solid #f1f1f1'
            }}
        >
            <div style={{ width: '10px', height: '10px', borderRadius: '3px', marginTop: '5px', flexShrink: 0, backgroundColor: tone.color }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                    {humanizeCheckName(check.name)}
                </div>
                <div style={{ fontSize: '12.5px', color: isFailed ? '#c62828' : '#6b7280', marginTop: '2px', fontFamily: isFailed ? 'monospace' : 'inherit' }}>
                    {check.detail || (isFailed ? check.error_code : '')}
                </div>
 
                {isExpanded && (
                    <div style={{ marginTop: '10px', padding: '12px 14px', backgroundColor: '#fafbfc', border: '1px solid #edeff2', borderRadius: '8px', fontSize: '12.5px', color: '#4b5563', display: 'grid', gap: '6px' }}>
                        {check.error_code && <div><strong style={{ color: '#111827' }}>Error code:</strong> {check.error_code}</div>}
                        {check.hint && <div><strong style={{ color: '#111827' }}>Hint:</strong> {check.hint}</div>}
                        {check.action && <div><strong style={{ color: '#111827' }}>Suggested action:</strong> {check.action}</div>}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <span style={{ color: tone.color, backgroundColor: tone.backgroundColor, fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px' }}>
                    {tone.label}
                </span>
                {isFailed && hasDetails && (
                    <button
                        type="button"
                        onClick={() => onToggleDetails(check.name)}
                        style={{ border: '1px solid #dde1e7', borderRadius: '6px', backgroundColor: '#fff', padding: '3px 10px', fontSize: '12px', fontWeight: 600, color: '#4b5563', cursor: 'pointer' }}
                    >
                        {isExpanded ? 'Hide' : 'Details'}
                    </button>
                )}
            </div>
        </div>
    );
};
 
/**
* Connection Test results popup. Renders either the real API response
* ({ source_bucket, checked_at, overall, checks: [...] }) or a request-level
* error message if the call itself failed.
*/
const TestConnectionModal = ({
    modalState, // { isOpen, account, result, error }
    isTestConnectionRunning,
    expandedCheckDetails,
    onToggleCheckDetails,
    onRunAgain,
    onClose
}) => {
    if (!modalState.isOpen) return null;
 
    const { account, result, error } = modalState;
    const accountId = result?.account_id || account?.account_id || '--';
 
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(17, 24, 39, 0.45)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2200,
                padding: '24px'
            }}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="test-connection-title"
                onClick={(event) => event.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: '720px',
                    maxHeight: '85vh',
                    backgroundColor: '#fff',
                    borderRadius: '14px',
                    boxShadow: '0 24px 60px rgba(17, 24, 39, 0.28)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div style={{ padding: '24px 28px 20px 28px', borderBottom: '1px solid #edeff2', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h3 id="test-connection-title" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                                    Connection test
                                </h3>
                                {result?.overall && (
                                    <span
                                        style={{
                                            ...getOverallTone(result.overall),
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            padding: '3px 10px',
                                            borderRadius: '999px',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        {result.overall}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#F2EEFB',
                                        color: '#5B45A0',
                                        border: '1px solid #E2D9F5',
                                        borderRadius: '999px',
                                        padding: '3px 11px 3px 8px',
                                        fontSize: '12.5px',
                                        fontWeight: 700,
                                        fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                                        letterSpacing: '0.02em'
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="7" width="18" height="13" rx="2" stroke="#5B45A0" strokeWidth="1.8" />
                                        <path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7" stroke="#5B45A0" strokeWidth="1.8" />
                                    </svg>
                                    {accountId}
                                </span>
                                {result?.checked_at && (
                                    <span style={{ fontSize: '12.5px', color: '#9ca3af' }}>
                                        {formatCheckedAt(result.checked_at)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                            <EDSButton variant="primary" type="button" label="Run again" onEDSButtonClick={onRunAgain} isDisabled={isTestConnectionRunning} />
                            <EDSButton variant="secondary" type="button" label="Close" onEDSButtonClick={onClose} isDisabled={isTestConnectionRunning} />
                        </div>
                    </div>
                </div>
 
                <div style={{ padding: '8px 28px 24px 28px', overflowY: 'auto' }}>
                    {error && !result && (
                        <div style={{ padding: '16px', marginTop: '16px', borderRadius: '8px', backgroundColor: '#ffebee', color: '#c62828', fontSize: '13.5px' }}>
                            {error}
                        </div>
                    )}
 
                    {result && Array.isArray(result.checks) && (
                        <div style={{ marginTop: '12px' }}>
                            {result.checks.map((check, index) => (
                                <CheckRow
                                    key={check.name || index}
                                    check={check}
                                    index={index}
                                    isExpanded={!!expandedCheckDetails[check.name]}
                                    onToggleDetails={onToggleCheckDetails}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
 
export default TestConnectionModal;